import { api } from '@/api';
import type { LatestZeroMeasurementDto } from '@/api/generator';
import { useDateTimeStore } from '@/stores/dateTimeStore';
import { useLoading } from '@/utils/useLoading';
import { DateTime } from 'luxon';
import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

/** How much of the validity window of the latest zero measurement is left. */
export interface ZeroMeasurementValidity {
  /** Seconds until the zero measurement expires. */
  remainingSeconds: number;
  /** Remaining share of the validity window, 1 right after the measurement. */
  remainingFraction: number;
}

/**
 * Loads the state of the latest zero measurement of an array + detector
 * interface pair and counts its validity down against the server clock.
 * `validity` is undefined without a zero measurement and once it has expired.
 * Loads whenever the ids change; `load` reloads on demand, e.g. after a zero
 * measurement dialog was closed. There is no push event for new zero
 * measurements, so nothing else refreshes the state.
 */
export function useLatestZeroMeasurement(
  arrayId: MaybeRefOrGetter<string | undefined>,
  detectorInterfaceId: MaybeRefOrGetter<string | undefined>,
) {
  const latest = ref<LatestZeroMeasurementDto | undefined>();

  // The server stamped the measurement, so the countdown runs against the server
  // clock: the store's clock is corrected by the session's Date header and ticks
  // every second, so no timer of its own is needed here. A workstation clock that
  // is minutes off would otherwise show an expired zero measurement as valid.
  // Only before the session is loaded is the clock unset; then the browser's
  // (also offset-corrected once the store is set, via Luxon's Settings.now).
  const dateTimeStore = useDateTimeStore();
  const now = computed(() => dateTimeStore.currentDateTime ?? DateTime.now());

  // Guards against a slow response for a previous pair overtaking the current one.
  let requestNumber = 0;

  const { action: load, loading } = useLoading(async () => {
    const currentArrayId = toValue(arrayId);
    const currentDetectorInterfaceId = toValue(detectorInterfaceId);
    const thisRequest = ++requestNumber;

    if (!currentArrayId || !currentDetectorInterfaceId) {
      latest.value = undefined;
      return;
    }

    const { success, result } = await api.zeroMeasurementsApi.getLatestZeroMeasurement({
      arrayId: currentArrayId,
      detectorInterfaceId: currentDetectorInterfaceId,
    });

    if (thisRequest === requestNumber) {
      latest.value = success ? result : undefined;
    }
  });

  watch([() => toValue(arrayId), () => toValue(detectorInterfaceId)], () => load(), {
    immediate: true,
  });

  const expiresAt = computed(() => {
    const executionTime = latest.value?.latestExecutionTime;
    if (!latest.value?.hasZeroMeasurement || !executionTime) {
      return undefined;
    }
    const executed = DateTime.fromISO(executionTime);
    return executed.isValid
      ? executed.plus({ seconds: latest.value.zeroMeasurementValidityDuration ?? 0 })
      : undefined;
  });

  const validity = computed<ZeroMeasurementValidity | undefined>(() => {
    if (!expiresAt.value) {
      return undefined;
    }
    const validityDuration = latest.value?.zeroMeasurementValidityDuration ?? 0;
    const remainingSeconds = expiresAt.value.diff(now.value).as('seconds');
    if (remainingSeconds <= 0 || validityDuration <= 0) {
      return undefined;
    }
    return {
      remainingSeconds,
      remainingFraction: Math.min(1, remainingSeconds / validityDuration),
    };
  });

  return { latest, validity, load, loading };
}
